import { parseNumberInput } from "@/lib/parse-number";

/**
 * Read a typed physical quantity: a plain number in the reader's notation, or scientific
 * notation such as `1.5e3`, `9.109e-31` or `6.02 × 10^23`.
 *
 * `parseNumberInput` deliberately rejects exponents; mechanics needs them for masses of
 * particles and energies in eV. Returns null for anything unreadable.
 */
const EXPONENT_FORM = /^(.+?)\s*(?:[eE]|[x×*]\s*10\s*\^?)\s*([+\-−]?\d{1,3})$/;
const MINUS = /−/g;

export function parseQuantity(raw: string): number | null {
  const plain = parseNumberInput(raw);
  if (plain !== null) {
    return plain;
  }
  const match = EXPONENT_FORM.exec(raw.trim());
  if (!match) {
    return null;
  }
  const mantissa = parseNumberInput(match[1]);
  const exponent = Number(match[2].replace(MINUS, "-"));
  if (mantissa === null || !Number.isFinite(exponent)) {
    return null;
  }
  // Building the literal keeps 6.02e23 exactly 6.02e23 (6.02 × 10 ** 23 drifts in the last bit).
  const literal = Number(`${mantissa}e${exponent}`);
  const value = Number.isNaN(literal) ? mantissa * 10 ** exponent : literal;
  return Number.isFinite(value) ? value : null;
}
